import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useFreelancerDisciplines } from "@/hooks/use-freelancers";
import { useSaveFreelancerDiscipline } from "@/hooks/use-freelancer-disciplines-admin";
import type { DBFreelancerDiscipline } from "@/lib/db-types";

function DisciplineRow({ d, count }: { d: DBFreelancerDiscipline; count: number }) {
  const [label, setLabel] = useState(d.label);
  const save = useSaveFreelancerDiscipline();
  const changed = label.trim() !== d.label;
  return (
    <div className="flex items-center gap-2 border-b border-border/50 py-2 last:border-0">
      <Input value={label} onChange={(e) => setLabel(e.target.value)} className="h-8 flex-1" aria-label={`Navn på ${d.label}`} />
      <span className="w-16 text-right text-xs text-muted-foreground tabular-nums">{count} stk</span>
      {changed && (
        <Button size="sm" variant="outline" onClick={() => save.mutate({ id: d.id, label })} disabled={save.isPending}>Lagre</Button>
      )}
      <Switch
        checked={d.is_active}
        onCheckedChange={(is_active) => save.mutate({ id: d.id, is_active })}
        aria-label={d.is_active ? `Deaktiver ${d.label}` : `Aktiver ${d.label}`}
      />
    </div>
  );
}

/** Admin: gi nytt navn, legg til og deaktiver fagfelt. Fagfelt i bruk slettes aldri, de skjules i velgeren. */
export function DisciplineAdminDialog({ open, onOpenChange, counts }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** discipline_id → antall frilansere. */
  counts: Map<string, number>;
}) {
  const { data: disciplines = [] } = useFreelancerDisciplines();
  const save = useSaveFreelancerDiscipline();
  const [newLabel, setNewLabel] = useState("");
  const nextOrder = Math.max(0, ...disciplines.map((d) => d.sort_order)) + 10;

  const add = () =>
    save.mutate({ label: newLabel, sort_order: nextOrder }, { onSuccess: () => setNewLabel("") });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Fagfelt</DialogTitle>
          <DialogDescription>Deaktiverte fagfelt skjules i velgeren, men blir stående på frilanserne som har dem.</DialogDescription>
        </DialogHeader>
        <div>
          {disciplines.map((d) => <DisciplineRow key={d.id} d={d} count={counts.get(d.id) ?? 0} />)}
        </div>
        <div className="flex gap-2">
          <Input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && newLabel.trim()) add(); }}
            placeholder="Nytt fagfelt …"
            aria-label="Nytt fagfelt"
          />
          <Button onClick={add} disabled={!newLabel.trim() || save.isPending}>Legg til</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
